/**
 * ============================================================================
 * [P0-02] Common Utilities - 순수 함수
 *
 * 일반적인 유틸리티 함수들
 * - DOM 의존성 없음 (순수 함수)
 * - TypeScript 타입 적용
 *
 * 기존 js/utils.js에서 추출 및 순수함수화
 * ============================================================================
 */

// ============================================================================
// [순수 함수] 디바운스
// - 연속적인 함수 호출을 지정된 시간 후 마지막 호출만 실행
// - DOM 의존성 없음 (setTimeout만 사용)
// ============================================================================
export function debounce<T extends (...args: Parameters<T>) => void>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: ReturnType<typeof setTimeout> | null = null

  return function executedFunction(...args: Parameters<T>): void {
    const later = (): void => {
      timeout = null
      func(...args)
    }

    if (timeout !== null) {
      clearTimeout(timeout)
    }
    timeout = setTimeout(later, wait)
  }
}

// ============================================================================
// [순수 함수] 쓰로틀
// - 지정된 시간 동안 최대 한 번만 함수 실행
// - DOM 의존성 없음
// ============================================================================
export function throttle<T extends (...args: Parameters<T>) => void>(
  func: T,
  limit: number
): (...args: Parameters<T>) => void {
  let inThrottle = false

  return function executedFunction(...args: Parameters<T>): void {
    if (!inThrottle) {
      func(...args)
      inThrottle = true
      setTimeout(() => {
        inThrottle = false
      }, limit)
    }
  }
}

// ============================================================================
// [순수 함수] 지수 백오프를 적용한 재시도
// - 비동기 함수 실행 실패 시 지수적으로 증가하는 딜레이로 재시도
// - Firestore 에러 코드 기반 재시도 가능 여부 판단
// ============================================================================
interface RetryableError extends Error {
  code?: string
}

const RETRYABLE_ERROR_CODES = [
  'unavailable',        // 서비스 일시 불가
  'resource-exhausted', // 할당량 초과
  'deadline-exceeded',  // 타임아웃
  'aborted',            // 트랜잭션 충돌
  'internal',           // 내부 오류
] as const

export async function withRetry<T>(
  fn: () => Promise<T>,
  maxRetries: number = 3,
  baseDelayMs: number = 1000
): Promise<T> {
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await fn()
    } catch (error) {
      const isLastAttempt = attempt === maxRetries
      const err = error as RetryableError

      // 재시도 가능한 에러인지 확인
      const isRetryable =
        RETRYABLE_ERROR_CODES.includes(err.code as typeof RETRYABLE_ERROR_CODES[number]) ||
        err.message === 'Failed to fetch' || // 네트워크 오류
        !err.code // 코드가 없는 일반 네트워크 에러

      if (isLastAttempt || !isRetryable) {
        throw error
      }

      // 지수 백오프 딜레이
      const delayMs = baseDelayMs * Math.pow(2, attempt)
      console.warn(
        `[Retry] 시도 ${attempt + 1}/${maxRetries} (${delayMs}ms 후 재시도)... 에러: ${err.message}`
      )
      await new Promise((resolve) => setTimeout(resolve, delayMs))
    }
  }

  // TypeScript 타입 안전을 위해 (실제로 도달하지 않음)
  throw new Error('withRetry: Unexpected end of function')
}

// ============================================================================
// [순수 함수] 에러 핸들러 래퍼 (동기)
// - try/catch를 간편하게 적용
// - DOM 의존성 없음
// ============================================================================
interface ErrorHandlerOptions {
  context?: string
  onError?: (error: Error) => void
}

export function withErrorHandler<T>(
  fn: () => T,
  options: ErrorHandlerOptions = {}
): T | null {
  const { context = 'Unknown', onError } = options

  try {
    return fn()
  } catch (error) {
    console.error(`[${context}]`, error)
    if (typeof onError === 'function') {
      onError(error as Error)
    }
    return null
  }
}

// ============================================================================
// [순수 함수] 에러 핸들러 래퍼 (비동기)
// - 비동기 함수에 try/catch를 간편하게 적용
// - DOM 의존성 없음
// ============================================================================
export async function withAsyncErrorHandler<T>(
  fn: () => Promise<T>,
  options: ErrorHandlerOptions = {}
): Promise<T | null> {
  const { context = 'Unknown', onError } = options

  try {
    return await fn()
  } catch (error) {
    console.error(`[${context}]`, error)
    if (typeof onError === 'function') {
      onError(error as Error)
    }
    return null
  }
}

// ============================================================================
// [순수 함수] 딜레이 (Promise 기반)
// - 지정된 시간만큼 대기
// ============================================================================
export function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

// ============================================================================
// [순수 함수] 고유 ID 생성
// - crypto API 사용 (브라우저/Node.js 환경 모두 지원)
// ============================================================================
export function generateId(): string {
  // crypto.randomUUID()가 지원되면 사용
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID()
  }

  // 폴백: 타임스탬프 + 랜덤 문자열
  return `${Date.now().toString(36)}-${Math.random().toString(36).substring(2, 11)}`
}

// ============================================================================
// [순수 함수] 객체 깊은 복사
// - structuredClone이 지원되면 사용, 아니면 JSON 파싱
// ============================================================================
export function deepClone<T>(obj: T): T {
  if (typeof structuredClone !== 'undefined') {
    return structuredClone(obj)
  }
  return JSON.parse(JSON.stringify(obj))
}

// ============================================================================
// [순수 함수] 빈 값 체크
// - null, undefined, 빈 문자열, 빈 배열, 빈 객체 확인
// ============================================================================
export function isEmpty(value: unknown): boolean {
  if (value === null || value === undefined) return true
  if (typeof value === 'string') return value.trim().length === 0
  if (Array.isArray(value)) return value.length === 0
  if (typeof value === 'object') return Object.keys(value).length === 0
  return false
}

// ============================================================================
// [순수 함수] 배열 청크 분할
// - 배열을 지정된 크기의 청크로 분할
// ============================================================================
export function chunkArray<T>(array: T[], chunkSize: number): T[][] {
  const chunks: T[][] = []
  for (let i = 0; i < array.length; i += chunkSize) {
    chunks.push(array.slice(i, i + chunkSize))
  }
  return chunks
}

// ============================================================================
// [순수 함수] 객체에서 특정 키만 선택
// ============================================================================
export function pick<T extends object, K extends keyof T>(
  obj: T,
  keys: K[]
): Pick<T, K> {
  const result = {} as Pick<T, K>
  for (const key of keys) {
    if (key in obj) {
      result[key] = obj[key]
    }
  }
  return result
}

// ============================================================================
// [순수 함수] 객체에서 특정 키 제외
// ============================================================================
export function omit<T extends object, K extends keyof T>(
  obj: T,
  keys: K[]
): Omit<T, K> {
  const result = { ...obj }
  for (const key of keys) {
    delete result[key]
  }
  return result as Omit<T, K>
}
